import { CommonModule, NgOptimizedImage } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import {
  Router,
  RouterLink,
  RouterLinkActive,
  RouterOutlet,
} from '@angular/router';
import {
  RdsAvatarComponent,
  RdsAvatarImageDirective,
  RdsButtonComponent,
  RdsCustomIconComponent,
  RdsDividerComponent,
  RdsDropdownDirective,
  RdsDropdownMenuDirective,
  RdsDropdownToggleButtonComponent,
  RdsDropdownToggleDirective,
  RdsHeaderActionsDirective,
  RdsHeaderAppNameDirective,
  RdsHeaderComponent,
  RdsHeaderEndDividerDirective,
  RdsHeaderLinkComponent,
  RdsHeaderNavigationPrimaryDirective,
  RdsHeaderStartDividerDirective,
  RdsIconButtonComponent,
  RdsIconComponent,
  RdsProgressSpinnerComponent,
  RdsTextButtonComponent,
} from '@rds/angular-components';

import {
  AuthService,
  ChartFilterMenuComponent,
  DestinationFrontendEnum,
  HasAccessDirective,
  NotificationsDropdownComponent,
  NotificationsService,
  PushNotificationsService,
  RoleRoutePipe,
  RoleType,
} from '@bookly/shared';
import { FavoriteIndicatorComponent } from '@bookly-user/rooms';

import { RoomFiltersMenuComponent } from '../rooms/components/room-filters-menu/room-filters-menu.component';

@Component({
  selector: 'bus-dashboard',
  imports: [
    CommonModule,
    NgOptimizedImage,
    NotificationsDropdownComponent,
    RdsAvatarComponent,
    RdsAvatarImageDirective,
    RouterOutlet,
    RdsIconComponent,
    RouterLink,
    RouterLinkActive,
    RoomFiltersMenuComponent,
    RdsProgressSpinnerComponent,
    ChartFilterMenuComponent,
    HasAccessDirective,
    RoleRoutePipe,
    RdsHeaderComponent,
    RdsHeaderStartDividerDirective,
    RdsHeaderAppNameDirective,
    RdsHeaderEndDividerDirective,
    RdsHeaderNavigationPrimaryDirective,
    RdsHeaderLinkComponent,
    RdsHeaderActionsDirective,
    RdsDividerComponent,
    RdsDropdownDirective,
    RdsDropdownToggleDirective,
    RdsDropdownMenuDirective,
    RdsTextButtonComponent,
    RdsIconButtonComponent,
    RdsDropdownToggleButtonComponent,
    RdsButtonComponent,
    FavoriteIndicatorComponent,
    RdsCustomIconComponent,
  ],
  templateUrl: './user-dashboard.component.html',
  styleUrl: './user-dashboard.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserDashboardComponent implements OnInit {
  readonly #authService = inject(AuthService);
  readonly #notificationService = inject(NotificationsService);
  readonly #pushNotificationService = inject(PushNotificationsService);
  protected readonly router = inject(Router);
  protected readonly user = this.#authService.user;
  protected readonly isLoading = this.#authService.isLoginPending;
  protected readonly RoleType = RoleType;

  @ViewChild('desktopDropdown', { read: RdsDropdownDirective })
  desktopMenuDropdown!: RdsDropdownDirective;

  @ViewChild('mobileDropdown', { read: RdsDropdownDirective })
  mobileMenuDropdown!: RdsDropdownDirective;

  ngOnInit(): void {
    void this.#pushNotificationService.init();
    this.#notificationService
      .getNotifications(DestinationFrontendEnum.User)
      .subscribe();
  }

  protected logout() {
    this.mobileMenuDropdown?.hide();
    this.desktopMenuDropdown?.hide();
    void this.#authService.logout();
  }

  protected navigateToHelpCenter() {
    window.open(
      'https://sites.google.com/contractors.roche.com/bookly/contact-support-basel',
      '_blank'
    );
  }

  protected navigateToFeedback() {
    window.open(
      'https://rocheps.qualtrics.com/jfe/form/SV_6o0xqTcQCcYzWcK',
      '_blank'
    );
  }

  protected showFiltersMenu(): boolean {
    const regex = /^\/dashboard\/rooms(?:\?[^\s/]*)?$/gm;
    return this.router.url.match(regex) !== null;
  }

  protected showChartsMenu(): boolean {
    const regex = /^\/dashboard\/8yd0whNE9HKhjAS(?:\?[^\s/]*)?$/gm;
    return this.router.url.match(regex) !== null;
  }
}
